import { Application, extend, useApplication, useTick } from '@pixi/react';
import { type Actor, type LifeStage, type TilePos, type World, specOf } from '@tama/game-core';
import { Container, type FederatedPointerEvent, Graphics, Rectangle } from 'pixi.js';
import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { type CameraMode, cameraTransform } from '../scene/camera';
import { catScaleFor, drawCat } from '../scene/cat';
import { drawObject } from '../scene/furniture';
import { TILE, depthOf, pointToTile, worldPixelSize } from '../scene/layout';
import { C } from '../scene/palette';
import { drawTiles } from '../scene/tiles';
import {
  type CatAnim,
  type Walker,
  createWalker,
  facingScale,
  reactWith,
  stepWalker,
} from '../scene/walker';

// @pixi/react v8 only knows the Pixi classes you hand it.
extend({ Container, Graphics });

/**
 * The world, drawn.
 *
 * This is the only file in the app that touches PixiJS. Everything above it
 * passes a `World` and where the pet is standing; everything below it is plain
 * drawing code and a pure interpolator.
 *
 * Depth is one number per drawable: `zIndex` is the y of its base, so the pet
 * walking below a table is drawn over it and the pet walking above it is drawn
 * behind. That is the whole of the 3/4 illusion.
 */

/** Walkable decor — rugs — sits just above the floor and never in front of the pet. */
const FLOOR_DECOR_Z = -1;
const OVERLAY_Z = 1_000_000;

export type TilePointerPhase = 'down' | 'move' | 'up';

export interface WorldStageEditing {
  /** The object whose footprint gets a selection outline, if any. */
  selectedObjectId: string | null;
  /**
   * Whether the current tool would succeed on `tile` — drives the cursor colour.
   * Called from the render loop, so it must be cheap and free of side effects.
   */
  isValidTarget: (tile: TilePos) => boolean;
  onTilePointer: (tile: TilePos, phase: TilePointerPhase) => void;
}

export interface WorldStageProps {
  world: World;
  actor: Actor;
  stage: LifeStage;
  /** Bumped by the parent to make the pet strike a pose; `key` is what triggers it. */
  reaction: { key: number; anim: CatAnim } | null;
  /** Dev mode. `null` in play mode, which also means the canvas takes no pointer events. */
  editing: WorldStageEditing | null;
}

const noDraw = () => {};

function CatActor({
  actor,
  stage,
  reaction,
  walkerRef,
}: Pick<WorldStageProps, 'actor' | 'stage' | 'reaction'> & {
  /**
   * Owned by `Scene`, not by this component: the camera follows the pet's
   * *interpolated* position, and that only exists here. Sharing the ref is what
   * lets the camera read it without a re-render per frame.
   */
  walkerRef: RefObject<Walker>;
}) {
  const graphicsRef = useRef<Graphics | null>(null);

  // Read the latest props inside the ticker without re-subscribing every render.
  const actorRef = useRef(actor);
  actorRef.current = actor;
  const stageRef = useRef(stage);
  stageRef.current = stage;

  const reactionKey = reaction?.key ?? 0;
  const reactionAnim = reaction?.anim;
  useEffect(() => {
    // The key starts at 0 and is bumped per reaction; ignore the mount value.
    if (reactionKey > 0 && reactionAnim) {
      walkerRef.current = reactWith(walkerRef.current, reactionAnim, 2.4);
    }
  }, [reactionKey, reactionAnim]);

  useTick((ticker) => {
    const walker = stepWalker(walkerRef.current, actorRef.current, ticker.deltaMS / 1000);
    walkerRef.current = walker;

    const g = graphicsRef.current;
    if (!g) return;

    // The cat is drawn in its own space with its feet at the origin; the
    // transform is what puts it on a tile and mirrors it.
    const scale = catScaleFor(stageRef.current);
    g.position.set(walker.x, walker.y);
    g.scale.set(scale * facingScale(walker.facing), scale);
    g.zIndex = depthOf(walker.y);
    drawCat(g, walker);
  });

  // `draw` is required by the element's props, but the cat is redrawn from the
  // ticker above via the ref — going through `draw` would mean a React render
  // per frame just to animate a blink.
  return <pixiGraphics ref={graphicsRef} draw={noDraw} zIndex={0} />;
}

/**
 * Dev mode's overlay: the grid, the spawn point, the selected object and the
 * tile under the cursor.
 *
 * Redrawn on the ticker rather than from React state because the hovered tile
 * changes on every pointer move, and a re-render per pixel of mouse travel is
 * exactly the sort of thing that makes an editor feel heavy.
 */
function EditorOverlay({ world, editing }: { world: World; editing: WorldStageEditing }) {
  const graphicsRef = useRef<Graphics | null>(null);
  const hoverRef = useRef<TilePos | null>(null);
  const worldRef = useRef(world);
  worldRef.current = world;
  const editingRef = useRef(editing);
  editingRef.current = editing;

  useTick(() => {
    const g = graphicsRef.current;
    if (!g) return;

    const current = worldRef.current;
    const { selectedObjectId, isValidTarget } = editingRef.current;
    const { width, height } = worldPixelSize(current);
    g.clear();

    for (let x = 1; x < current.width; x++) {
      g.rect(x * TILE, 0, 1, height).fill({ color: C.gridLine, alpha: 0.14 });
    }
    for (let y = 1; y < current.height; y++) {
      g.rect(0, y * TILE, width, 1).fill({ color: C.gridLine, alpha: 0.14 });
    }

    // Where the pet appears when the map is entered. Worth seeing while editing:
    // it is the one tile a wall must never be painted over.
    const spawn = current.spawn;
    g.circle(spawn.x * TILE + TILE / 2, spawn.y * TILE + TILE / 2, 6).stroke({
      color: C.spawnMark,
      width: 2,
    });

    const selected = current.objects.find((object) => object.id === selectedObjectId);
    if (selected) {
      const spec = specOf(selected);
      g.rect(
        selected.x * TILE + 1,
        selected.y * TILE + 1,
        spec.width * TILE - 2,
        spec.height * TILE - 2,
      ).stroke({ color: C.selection, width: 2.5 });
    }

    const hover = hoverRef.current;
    if (hover) {
      const color = isValidTarget(hover) ? C.cursorOk : C.cursorBad;
      g.rect(hover.x * TILE + 1, hover.y * TILE + 1, TILE - 2, TILE - 2).fill({
        color,
        alpha: 0.22,
      });
      g.rect(hover.x * TILE + 1, hover.y * TILE + 1, TILE - 2, TILE - 2).stroke({
        color,
        width: 2,
      });
    }
  });

  const hitArea = useMemo(() => {
    const { width, height } = worldPixelSize(world);
    return new Rectangle(0, 0, width, height);
  }, [world]);

  const report = useCallback((event: FederatedPointerEvent, phase: TilePointerPhase) => {
    const local = event.getLocalPosition(event.currentTarget as Container);
    const tile = pointToTile(local.x, local.y);
    hoverRef.current = tile;
    editingRef.current.onTilePointer(tile, phase);
  }, []);

  return (
    <>
      <pixiGraphics ref={graphicsRef} draw={noDraw} zIndex={OVERLAY_Z} />
      {/*
        An explicit hit area rather than a transparent filled shape: an invisible
        Graphics is easy to get subtly wrong, and the map's bounds are known.
        It sits above the overlay so nothing can steal a click from it.
      */}
      <pixiContainer
        eventMode="static"
        hitArea={hitArea}
        zIndex={OVERLAY_Z + 1}
        onPointerDown={(event: FederatedPointerEvent) => report(event, 'down')}
        onPointerMove={(event: FederatedPointerEvent) => report(event, 'move')}
        onPointerUp={(event: FederatedPointerEvent) => report(event, 'up')}
        onPointerUpOutside={(event: FederatedPointerEvent) => report(event, 'up')}
        onPointerLeave={() => {
          hoverRef.current = null;
        }}
      />
    </>
  );
}

/**
 * Places the map in the canvas, whatever size that currently is.
 *
 * The camera is recomputed on the ticker rather than from React state because
 * two of its three inputs change without React hearing about it: the canvas
 * resizes itself (`resizeTo`) — including on rotation — and the pet's position
 * is interpolated frame by frame.
 */
function Scene({ world, actor, stage, reaction, editing }: WorldStageProps) {
  const containerRef = useRef<Container | null>(null);
  const walkerRef = useRef<Walker>(createWalker(actor));
  const { app } = useApplication();

  const tiles = useCallback((g: Graphics) => drawTiles(g, world), [world]);

  // Furniture is static: each piece draws once and then only when it moves.
  // Only the pet and the editor overlay are rebuilt per frame.
  const objects = useMemo(
    () =>
      world.objects.map((object) => {
        const spec = specOf(object);
        return (
          <pixiGraphics
            key={object.id}
            draw={(g: Graphics) => {
              g.clear();
              drawObject(g, object);
            }}
            zIndex={spec.solid ? depthOf((object.y + spec.height) * TILE) : FLOOR_DECOR_Z}
          />
        );
      }),
    [world.objects],
  );

  const worldRef = useRef(world);
  worldRef.current = world;

  /**
   * Pixi's `resizeTo` listens to the *window*, not to the element it was given.
   * Opening the editor puts the map in a smaller grid cell without the window
   * changing size at all, so the canvas kept its old dimensions and the map
   * rendered at the old scale, cropped by the frame. Watching the host element
   * is what makes the map follow its container.
   */
  useEffect(() => {
    const host = app?.canvas?.parentElement;
    if (!app || !host) return;

    const observer = new ResizeObserver(() => app.resize());
    observer.observe(host);
    return () => observer.disconnect();
  }, [app]);

  // The whole map, always, while it is being drawn on — see `CameraMode`. The
  // ref is read from the ticker, where the prop itself would be stale.
  const modeRef = useRef<CameraMode>('fit');
  modeRef.current = editing ? 'fit' : 'cover';

  useTick(() => {
    const container = containerRef.current;
    const screen = app?.screen;
    if (!container || !screen) return;

    const walker = walkerRef.current;
    const camera = cameraTransform(modeRef.current, screen, worldPixelSize(worldRef.current), {
      x: walker.x,
      // The walker's y is the pet's *feet*. Centring on those hangs it low in
      // the frame, so the camera aims at its middle instead.
      y: walker.y - TILE * 0.5,
    });

    container.scale.set(camera.scale);
    // Deliberately not rounded to whole pixels. Quantising the camera but not
    // the pet — which is positioned from the same interpolated walker — makes
    // the cat jitter by a pixel against the floor as the room pans behind it.
    container.position.set(camera.x, camera.y);
  });

  return (
    <pixiContainer ref={containerRef} sortableChildren>
      <pixiGraphics draw={tiles} zIndex={-2} />
      {objects}
      <CatActor actor={actor} stage={stage} reaction={reaction} walkerRef={walkerRef} />
      {editing && <EditorOverlay world={world} editing={editing} />}
    </pixiContainer>
  );
}

export function WorldStage(props: WorldStageProps) {
  const hostRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={hostRef}
      className="world-stage"
      data-testid="world-stage"
      data-editing={props.editing ? 'true' : 'false'}
    >
      <Application
        resizeTo={hostRef}
        backgroundAlpha={0}
        antialias
        resolution={window.devicePixelRatio || 1}
        autoDensity
      >
        <Scene {...props} />
      </Application>
    </div>
  );
}
